import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import {App} from './app/App';
import './theme/global.css';

/**
 * Entry point.
 *
 * No service worker and no web-app manifest, on purpose. 03 is explicit that
 * there is no install: a guest who is prompted to add something to their home
 * screen before they can read a menu is a guest who puts the phone down. The
 * only caching this surface gets is HTTP caching and the conditional menu read.
 *
 * # `#root` is NOT empty when this runs
 *
 * `index.html` pre-fills it with the first-paint skeleton — see
 * `src/theme/ThemeStyles.tsx` for why that block exists at all — and the render
 * below is what removes it: `createRoot(container).render(...)` clears the
 * container's existing children before mounting. Nothing else deletes those
 * nodes, and nothing needs to.
 *
 * A sibling `<div id="boot">` removed by an explicit line here was the
 * alternative, and it is worse in the case that matters. That removal code
 * would live in this same bundle, so it can only run once the bundle the guest
 * is waiting for has arrived — and if anything above it throws, the grey bars
 * stay on screen above whatever the ErrorBoundary renders. Children of the
 * container cannot survive their own replacement.
 *
 * The skeleton carries no text, and that is a language decision rather than a
 * design one: the guest's language lives in `localStorage` behind `src/i18n`,
 * which is in the bundle that has not arrived. A hard-coded "Loading…" would be
 * English shown to a Thai guest or Thai shown to a tourist, and choosing one is
 * not something the static shell is allowed to do. So it is `aria-hidden` and
 * silent until the screens' own live regions take over.
 *
 * `test/guest-defects.test.tsx` mounts the real `App` into a container seeded
 * with markup lifted out of `index.html` and asserts none of it survives —
 * because "createRoot clears the container" is react-dom behaviour this app
 * depends on and does not control, and a move to `hydrateRoot` would change it
 * without saying so.
 */
const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
